'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import { selectAuth } from '@/src/redux/slices/authSlice'
import { useAppSelector } from '@/src/redux/store'
import { useGetAccountProfileQuery } from '@/src/api/rtkQuery/profileApi'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { transformProfileResponse } from '@/src/utils/profile/transformProfileResponse'
import { buildTravellerDisplay } from '@/src/utils/travellers/buildTravellerDisplay'
import { isCleartrip } from '@/src/utils/generalUtils/isCleartrip'
import { getItineraryInfoPath } from '@/src/utils/navigation/getItineraryInfoPath'

import { MobileProfileView } from './profile'
import { EditProfileWrapper } from '@/src/components/desktop/accounts/profile/editProfile'
import useRedirectToHome from '@/src/hooks/navigation/useRedirectToHome'

const SignInModalWrapper = dynamic(
    () => import(/* webpackChunkName: "signin-modal" */ '../../common/header/variants/flyin/SignInModal/SignInModalWrapper'),
    { ssr: false }
)

const MobileProfilePageWrapper: React.FC = () => {
    const t = useTranslations('account.profile')
    const locale = useLocale()
    const isRtl = locale === 'ar'
    const router = useRouter()
    const goToHome = useRedirectToHome()
    const authState = useAppSelector(selectAuth)
    const isLoggedIn = authState.signedIn
    const { formatDate } = useFormatDate()
    const { data: rawProfile, isLoading, isError, refetch } = useGetAccountProfileQuery({
        fetchControl: isCleartrip() ? 'MAIN' : 'ALL'
    })

    const [isMounted, setIsMounted] = useState(false)
    const [isSignInOpen, setIsSignInOpen] = useState(false)
    const isBackTrappedRef = useRef(false)

    // When a flow hands us an itineraryId, park a duplicate history entry so the first back press
    // pops back onto this page instead of leaving it. That lets us send the user to the flow
    // they came from rather than whatever sits behind us in history - on mobile that is the
    // travellers page, which is not where the coupon they need to re-apply lives.
    useEffect(() => {
        const returnPath = getItineraryInfoPath(
            new URLSearchParams(window.location.search).get('itineraryId'),
            locale
        )
        if (!returnPath) return

        if (!isBackTrappedRef.current) {
            window.history.pushState(null, '', window.location.href)
            isBackTrappedRef.current = true
        }

        const handlePopState = () => {
            router.replace(returnPath)
        }
        window.addEventListener('popstate', handlePopState)
        return () => window.removeEventListener('popstate', handlePopState)
    }, [router, locale])
    const [isEditing, setIsEditing] = useState(false)

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsMounted(true)
        }
    }, [])

    useEffect(() => {
        if (isMounted && !isLoggedIn) {
            setIsSignInOpen(true)
        }
    }, [isMounted, isLoggedIn])

    const handleSignInClose = useCallback(() => {
        setIsSignInOpen(false)
        if (!isLoggedIn) {
            goToHome({ isReloadRequired: false })
        }
    }, [isLoggedIn, goToHome])

    const profileForView = useMemo(() => {
        if (!rawProfile) return null
        return transformProfileResponse(rawProfile)
    }, [rawProfile])

    const derived = useMemo(() => {
        if (!profileForView) return null
        const { displayName: fullName, initials } = buildTravellerDisplay({
            title: profileForView.title,
            firstName: profileForView.firstName,
            lastName: profileForView.lastName
        })

        const phoneLabel = profileForView.phone?.number ? `+${profileForView.phone.countryCode} ${profileForView.phone.number}` : '--'
        const dateOfBirthValue = profileForView.dateOfBirth ? formatDate(profileForView.dateOfBirth, 'DD MMMM YYYY') : '--'

        return {
            fullName,
            initials,
            email: profileForView.email,
            phoneLabel,
            gender: profileForView.gender,
            screenName: profileForView.screenName || '--',
            dateOfBirthValue,
            addressLabel: profileForView.addressLabel || '--'
        }
    }, [profileForView, formatDate])

    const onBack = useCallback(() => {
        // Flows that cannot afford to lose their place (the Heya coupon modal sends people here
        // mid checkout) pass an itineraryId, otherwise back means home as before
        const returnPath = getItineraryInfoPath(
            new URLSearchParams(window.location.search).get('itineraryId'),
            locale
        )
        if (returnPath) {
            // Go through history when the trap is installed so this arrow and the hardware back
            // button land in the same place and no spare entry is left behind
            if (isBackTrappedRef.current) {
                window.history.back()
            } else {
                router.replace(returnPath)
            }
            return
        }
        goToHome({ isReloadRequired: false })
    }, [router, locale, goToHome])

    const renderContent = () => {
        if (isLoading) {
            return (
                <Box sx={{ minHeight: '100dvh', display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            )
        }

        if ((isError || !derived) && !isLoading) {
            return (
                <Box sx={{ px: 2, pt: 2 }}>
                    <Alert
                        severity="error"
                        sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}
                        action={
                            <Button color="inherit" size="small" onClick={refetch} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                {t('retry')}
                            </Button>
                        }
                    >
                        {t('failedToLoad')}
                    </Alert>
                </Box>
            )
        }

    const resolvedProfile = profileForView
    const resolvedDerived = derived

    if (!resolvedProfile || !resolvedDerived) {
        return null
    }

        if (isEditing) {
            return (
                <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ borderBottom: 1, borderColor: 'divider', px: 2, py: 1.5, position: 'relative' }}
                    >
                        <IconButton aria-label={t('editTitle')} onClick={() => setIsEditing(false)} sx={{ ml: -1 }}>
                            <ArrowBackIcon sx={{ fontSize: 20, transform: isRtl ? 'scaleX(-1)' : 'none' }} />
                        </IconButton>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                fontSize: '16px',
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)'
                            }}
                        >
                            {t('editTitle')}
                        </Typography>
                        <Box sx={{ width: 40 }} />
                    </Stack>
                    <Box sx={{ px: 1.5, py: 2 }}>
                        <EditProfileWrapper
                            initialProfile={resolvedProfile}
                            hideHeading
                            onCancel={() => setIsEditing(false)}
                            onSave={() => setIsEditing(false)}
                        />
                    </Box>
                </Box>
            )
        }

        return (
            <MobileProfileView
                t={t}
                profile={profileForView}
                fullName={derived.fullName}
                initials={derived.initials}
                phoneLabel={derived.phoneLabel}
                dateOfBirthValue={derived.dateOfBirthValue}
                onBack={onBack}
                onEdit={() => setIsEditing(true)}
                isRtl={isRtl}
            />
        )
    }

    return (
        <>
            {renderContent()}
            {isSignInOpen && <SignInModalWrapper open={isSignInOpen} onClose={handleSignInClose} />}
        </>
    )
}

export { MobileProfilePageWrapper }
export default MobileProfilePageWrapper
